import { useLayoutEffect, useRef, useState } from "react";
import { getComputed, useThemeChange } from "./Theme";
import { useTranslation } from "react-i18next";
import gsap from "gsap";

export const COLSYN = (
  <>
    <span className="font-bold font-COLSYN">
      <span className="text-green-400">C</span>
      <span className="text-sky-400">O</span>
      <span className="text-red-400">L</span>
      <span className="text-amber-400">S</span>
      <span className="text-violet-400">Y</span>
      <span className="text-pink-400">N</span>
    </span>
  </>
);
export const CSICK = <span className="font-bold font-kimaru">CSICK</span>;
export const EldoM = <span className="font-bold font-ELDOM">EldoM</span>;
export const SEQ_01 = <span className="font-bold font-SEQ-01">SEQ-01</span>;
export const kimaru = <span className="font-bold font-kimaru">kimaru</span>;

export function TextShuffle({
  text,
  trigger,
}: {
  text: string;
  trigger: string;
}) {
  const [textColor, setTextColor] = useState(
    getComputed(document.documentElement, "--color-text"),
  );
  const [themeChanges, setThemeChanges] = useState(0);
  const accentColors = [
    "--color-accent-pink",
    "--color-accent-cyan",
    "--color-accent-yellow",
    "--color-accent-orange",
    "--color-accent-green",
    "--color-accent-blue",
    "--color-accent-purple",
  ];
  useThemeChange(() => {
    setTextColor(getComputed(document.documentElement, "--color-text"));
    setThemeChanges((v) => v + 1); // force re-render and skip animation
  });
  const { t } = useTranslation();
  const hasRendered = useRef(false);
  const tlRef = useRef<gsap.core.Timeline | null>(null);

  const letterMap = (message: string, className: string, isName = false) => {
    let color = undefined;
    if (hasRendered.current) {
      color = textColor;
    }
    return message.split("").map((char, index) => (
      <span
        key={index}
        className={className}
        style={{
          opacity: 0,
          color: color,
        }}
      >
        {char}
      </span>
    ));
  };

  const letters = () => {
    return letterMap(text, "shuffle-letter", true);
  };

  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const shuffleLetters =
      ref.current?.querySelectorAll(".shuffle-letter") ?? [];
    hasRendered.current = true;

    if (tlRef.current) {
      tlRef.current.kill();
      const shuffleLetters = gsap.utils.toArray<Element>(".shuffle-letter");
      gsap.set(shuffleLetters, { opacity: 1, color: textColor });
      gsap.set(".shuffle-letter", { opacity: 1, color: textColor });
      return;
    }

    const tl = gsap.timeline({
      repeat: 0,
      scrollTrigger: { trigger: trigger },
    });
    tlRef.current = tl;

    shuffleLetters.forEach((letter, index) => {
      const shuffledColors = gsap.utils.shuffle([...accentColors]);
      tl.to(
        letter,
        {
          keyframes: [
            ...shuffledColors.map((color) => ({ color: `var(${color})` })),
            { color: textColor },
          ],
          opacity: 1,
          duration: 0.7,
          ease: "none",
        },
        index * 0.1,
      );
    });
  }, [t, themeChanges]);

  return <span ref={ref}>{letters()}</span>;
}

export function SlideIn({
  trigger,
  children,
  duration = 0.5,
  from = "bottom",
  elementType = "div",
  className = "",
}: {
  trigger: string;
  children: React.ReactNode;
  duration?: number;
  from?: "bottom" | "top" | "left" | "right";
  elementType?: "div" | "section" | "span";
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    switch (from) {
      case "bottom":
        gsap.set(ref.current, { y: 40, opacity: 0 });
        break;
      case "top":
        gsap.set(ref.current, { y: -40, opacity: 0 });
        break;
      case "left":
        gsap.set(ref.current, { x: -40, opacity: 0 });
        break;
      case "right":
        gsap.set(ref.current, { x: 40, opacity: 0 });
        break;
    }
    gsap.to(ref.current, {
      y: 0,
      x: 0,
      opacity: 1,
      duration: duration,
      ease: "power2.out",
      scrollTrigger: { trigger: trigger, start: "top 80%" },
    });
  }, []);
  switch (elementType) {
    case "section":
      return (
        <section
          className={`slide-in opacity-0 translate-y-10 ${className}`}
          ref={ref}
        >
          {children}
        </section>
      );
    case "span":
      return (
        <span
          className={`slide-in opacity-0 translate-y-10 ${className}`}
          ref={ref}
        >
          {children}
        </span>
      );
    default:
      return (
        <div
          className={`slide-in opacity-0 translate-y-10 ${className}`}
          ref={ref}
        >
          {children}
        </div>
      );
  }
}
